import React, { useState } from 'react';
import { Play, Book, Video, CheckCircle, ChevronRight, Download, Users, FileText, DollarSign, BarChart, Star, MessageSquare, Link as LinkIcon, Camera, Mail, FileSignature, Mic, UserCog } from 'lucide-react';
import { Link } from 'react-router-dom';

const Tutorial = () => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const tutorials = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <Book className="w-6 h-6" />,
      duration: '10 min',
      lessons: [
        {
          title: '1. Create Your Account',
          content: [
            'Visit the Sign Up page and register with your business email',
            'Choose your subscription plan (Free Trial, Pro, or Enterprise)',
            'Complete your profile with company information',
            'Verify your email address to activate your account'
          ]
        },
        {
          title: '2. Dashboard Overview',
          content: [
            'Navigate the main dashboard to see all features',
            'View your recent estimates and projects',
            'Access quick actions for common tasks',
            'Check your subscription status and usage'
          ]
        },
        {
          title: '3. First Measurement',
          content: [
            'Click "New Estimate" in the dashboard',
            'Enter a property address',
            'Wait 30 seconds for satellite analysis',
            'Review measurements and pricing',
            'Save or send the estimate to your customer'
          ]
        }
      ]
    },
    {
      id: 'entering-rates',
      title: 'Entering Your Rates',
      icon: <DollarSign className="w-6 h-6" />,
      duration: '10 min',
      lessons: [
        {
          title: '1. Navigate to My Rates',
          content: [
            'Click "My Rates" in the sidebar navigation',
            'This is where you set your material and labor pricing',
            'Rates are used to calculate all estimates automatically',
            'You can update rates at any time — existing estimates are not affected'
          ]
        },
        {
          title: '2. Understanding Rate Types',
          content: [
            'Material Rate: Cost of roofing materials (shingles, underlayment, flashing, etc.)',
            'Labor Rate: Cost of installation labor per unit',
            'You can price per Square Foot (sqft) or per Square (100 sqft) — select your preference',
            'Overhead & Profit (O&P): Percentage added on top of materials + labor',
            'Sales Tax: Automatically applied to the total estimate'
          ]
        },
        {
          title: '3. Setting Rates by Roof Type',
          content: [
            'Set different rates for each roofing material type:',
            '• Landmark (Baseline Architectural Shingles)',
            '• Landmark PRO (Premium Architectural)',
            '• Standing Seam Metal',
            '• Luxury Line (Designer/Premium)',
            'Each type has its own material and labor rate fields',
            'When creating an estimate, select the roof type and your rates are applied automatically'
          ]
        },
        {
          title: '4. Additional Pricing Components',
          content: [
            'Steep Roof Charge: Add a percentage or flat fee for steep-pitch roofs',
            'Multi-Story Charge: Extra cost for 2+ story buildings',
            'Gutters & Guards: Priced per linear foot ($11/LF for 5", $13/LF for 6") — includes materials + labor',
            'Downspouts: Enter count and linear feet with custom price per LF',
            'Other Charges: Add any custom line items (permits, debris removal, etc.)',
            'All additional charges include sales tax automatically'
          ]
        },
        {
          title: '5. Tips for Accurate Pricing',
          content: [
            'Review your supplier pricing quarterly and update rates accordingly',
            'Use the "per square" option if your suppliers quote per roofing square',
            'Include waste factor in your material rates (system adds ~12.5% waste to measurements)',
            'Test with a known address to verify your estimates match your manual calculations',
            'Use the Detailed Report on any estimate to see the full line-item breakdown'
          ]
        }
      ]
    },
    {
      id: 'estimates',
      title: 'Creating Estimates',
      icon: <FileText className="w-6 h-6" />,
      duration: '15 min',
      lessons: [
        {
          title: '1. Generate Roof Measurements',
          content: [
            'Enter the property address in the estimate form',
            'System automatically fetches satellite imagery',
            'Review roof measurements: square footage, pitch, squares',
            'Verify accuracy rating (typically 96-98%)',
            'View satellite map overlay of the property'
          ]
        },
        {
          title: '2. Customize Pricing',
          content: [
            'System auto-calculates materials and labor costs',
            'Adjust pricing based on your rates in Settings > My Rates',
            'Add custom line items for additional work',
            'Apply discounts or promotional pricing',
            'Set overhead and profit margins'
          ]
        },
        {
          title: '3. Send to Customer',
          content: [
            'Click "Generate PDF" to create professional estimate',
            'Email directly to customer from the platform',
            'Share public link for online viewing',
            'Track when customer views the estimate',
            'Follow up with automated reminders'
          ]
        }
      ]
    },
    {
      id: 'lead-pipeline',
      title: 'Lead Pipeline (CRM)',
      icon: <Users className="w-6 h-6" />,
      duration: '20 min',
      badge: 'NEW',
      lessons: [
        {
          title: '1. Understanding the Pipeline',
          content: [
            'Navigate to Dashboard > Lead Pipeline (CRM)',
            'View leads organized in columns by stage: New Lead → Contacted → Quoted → Site Visit → Negotiating → Won/Lost',
            'Each card shows customer name, address, estimated value, and notes',
            'Click any lead card to view full details and update information'
          ]
        },
        {
          title: '2. Adding & Managing Leads',
          content: [
            'Click "Add Lead" button to manually create a new lead',
            'Enter customer name, email, phone, address, and estimated value',
            'Select initial stage and add notes about the lead',
            'Leads from instant estimates are automatically added to your pipeline'
          ]
        },
        {
          title: '3. Updating Lead Stages',
          content: [
            'Click on a lead card to open the detail modal',
            'Click stage buttons to move lead through the pipeline',
            'Add notes about why lead was lost or special requests',
            'Use the List View for bulk stage updates with Save button'
          ]
        },
        {
          title: '4. Audio Notes (Voice Recording)',
          content: [
            'In the lead detail modal, find the "Contractor Notes" section',
            'Click "Enable Microphone" to grant permission (first time only)',
            'Click "Record Note" to start voice recording',
            'Click "Stop" when finished, then "Transcribe" to convert to text',
            'Edit the transcription if needed and click "Save Notes"'
          ]
        },
        {
          title: '5. Pipeline Reports',
          content: [
            'Click the "Report" tab to view pipeline analytics',
            'Select date range: Week, Month, Quarter, Year, or Custom',
            'View stage progression with checkmarks for each lead',
            'See totals for estimates, won deals, and lost leads',
            'Export to CSV or PDF for record keeping'
          ]
        },
        {
          title: '6. Admin: Multi-User Pipeline Management',
          content: [
            'Admins see "Admin View" badge and user filter dropdown',
            'Select "All Team Members" to view entire company pipeline',
            'Click on any lead to reassign it to another team member',
            'Use "Group by User" toggle in reports for team breakdown',
            'Track individual and combined team performance'
          ]
        }
      ]
    },
    {
      id: 'leadgen',
      title: 'Lead Generation',
      icon: <LinkIcon className="w-6 h-6" />,
      duration: '20 min',
      lessons: [
        {
          title: '1. Set Up Your Landing Page',
          content: [
            'Go to Dashboard > Lead Generation',
            'Click "Setup" tab to configure your page',
            'Enter company name, logo, and branding colors',
            'Add contact information and custom message',
            'Set your unique username for the page URL'
          ]
        },
        {
          title: '2. Embed on Your Website',
          content: [
            'Copy the embed code from the "Embed" section',
            'Paste into your website HTML',
            'OR share the standalone landing page URL',
            'Customize colors to match your brand',
            'Test the form to ensure it works correctly'
          ]
        },
        {
          title: '3. Manage Incoming Leads',
          content: [
            'View captured leads in the "Leads" tab',
            'See customer name, address, contact info',
            'Click "View Estimate" to see generated measurement',
            'Update lead status (New, Contacted, Qualified)',
            'Add notes for follow-up tracking'
          ]
        },
        {
          title: '4. Automated Follow-ups',
          content: [
            'System automatically emails customers with estimate link',
            'Follow-up sent 6 hours after measurement request',
            'Second follow-up sent 24 hours after initial contact',
            'Emails include promotional offers (5% discount)',
            'Track email open rates and customer engagement'
          ]
        }
      ]
    },
    {
      id: 'fieldcam',
      title: 'FieldCam AI',
      icon: <Camera className="w-6 h-6" />,
      duration: '20 min',
      badge: 'NEW',
      lessons: [
        {
          title: '1. What is FieldCam AI?',
          content: [
            'FieldCam is a mobile-optimized tool for documenting roof damage on-site',
            'Record audio or video notes while inspecting the property',
            'Take photos of damage areas with descriptions',
            'AI automatically transcribes your voice notes',
            'Generate professional inspection reports with one click'
          ]
        },
        {
          title: '2. Recording Audio Notes',
          content: [
            'Navigate to Dashboard > FieldCam AI',
            'Select "Audio" recording mode (default)',
            'Click the red microphone button to start recording',
            'Describe damage as you walk the property',
            'Click "Stop" when finished, then "Transcribe" for AI processing'
          ]
        },
        {
          title: '3. Recording Video Notes',
          content: [
            'Toggle to "Video" recording mode',
            'Grant camera and microphone permissions when prompted',
            'Record video while narrating the damage you see',
            'Video is saved and can be attached to reports',
            'Great for insurance documentation and customer communication'
          ]
        },
        {
          title: '4. Taking & Organizing Photos',
          content: [
            'Click the camera icon or "Take Photo" button',
            'Capture damage areas, problem spots, and overall condition',
            'Add descriptions to each photo explaining the issue',
            'Photos are automatically organized with the inspection',
            'Drag to reorder photos as needed'
          ]
        },
        {
          title: '5. Generating AI Reports',
          content: [
            'After recording and adding photos, click "Generate AI Report"',
            'AI analyzes your notes and creates a professional summary',
            'Report includes damage assessment, recommendations, and next steps',
            'Download as PDF or attach directly to insurance claims',
            'Edit report content before finalizing if needed'
          ]
        },
        {
          title: '6. Linking to Insurance Claims',
          content: [
            'Access FieldCam from an insurance claim by clicking "FieldCam" button',
            'All photos and reports automatically link to the claim',
            'Field documentation appears in the claim detail page',
            'Streamlines the insurance submission process'
          ]
        }
      ]
    },
    {
      id: 'email-funnel',
      title: 'Email Marketing Funnel',
      icon: <Mail className="w-6 h-6" />,
      duration: '15 min',
      badge: 'NEW',
      lessons: [
        {
          title: '1. Understanding Email Funnels',
          content: [
            'Email funnels automatically nurture leads with timed emails',
            'Triggered when customers request estimates or show interest',
            'Multiple email templates sent at strategic intervals',
            'AI-powered email assistant helps write compelling content',
            'Track open rates, clicks, and conversions'
          ]
        },
        {
          title: '2. Configuring Your Funnel',
          content: [
            'Navigate to Dashboard > Email Management',
            'View default email templates in the funnel sequence',
            'Customize subject lines to increase open rates',
            'Edit email body with your unique selling points',
            'Set timing between emails (hours or days)'
          ]
        },
        {
          title: '3. Using the AI Email Assistant',
          content: [
            'Click "AI Assistant" button to open the chat',
            'Describe what you want to say to customers',
            'AI generates professional email copy',
            'Review and edit the suggestion as needed',
            'Copy directly into your email template'
          ]
        },
        {
          title: '4. Email Template Variables',
          content: [
            'Use {{customer_name}} to personalize greetings',
            'Include {{estimate_link}} for direct estimate access',
            'Add {{company_name}} for consistent branding',
            'Use {{discount_code}} for promotional offers',
            'Variables are automatically replaced when emails send'
          ]
        },
        {
          title: '5. Monitoring Performance',
          content: [
            'View funnel statistics on the main email management page',
            'Track emails sent, opened, and clicked',
            'See conversion rates for each email in the sequence',
            'Pause or resume the funnel as needed',
            'A/B test different subject lines and content'
          ]
        }
      ]
    },
    {
      id: 'contracts',
      title: 'Contract Templates',
      icon: <FileSignature className="w-6 h-6" />,
      duration: '15 min',
      badge: 'NEW',
      lessons: [
        {
          title: '1. Uploading Contract Templates',
          content: [
            'Navigate to Dashboard > Contract Templates',
            'Click "Upload Template" to add your contract documents',
            'Supported formats: PDF, DOCX, DOC',
            'Give each template a name and description',
            'Upload multiple templates for different job types'
          ]
        },
        {
          title: '2. Setting Active Template',
          content: [
            'View all uploaded templates in the list',
            'Click "Set as Active" to make a template your default',
            'Active template is automatically attached to new estimates',
            'You can change the active template anytime',
            'Previous contracts remain linked to their original templates'
          ]
        },
        {
          title: '3. Using Templates with Estimates',
          content: [
            'When creating an estimate, contract is auto-attached',
            'Customer receives estimate with contract for e-signature',
            'Track contract status: Sent, Viewed, Signed',
            'Download signed contracts from the estimate detail page',
            'Both parties receive copies of signed agreements'
          ]
        },
        {
          title: '4. Managing Templates',
          content: [
            'Preview any template by clicking the eye icon',
            'Download templates with the download button',
            'Delete outdated templates (linked contracts preserved)',
            'Keep templates organized with clear naming conventions',
            'Update templates when contract terms change'
          ]
        }
      ]
    },
    {
      id: 'insurance',
      title: 'InsureTrax Claims',
      icon: <DollarSign className="w-6 h-6" />,
      duration: '25 min',
      lessons: [
        {
          title: '1. Create Insurance Claim',
          content: [
            'Navigate to Dashboard > Insurance Claims',
            'Click "New Claim" to start',
            'Enter customer and property information',
            'Add insurance company details and policy number',
            'Upload storm damage photos (supports S3 storage)'
          ]
        },
        {
          title: '2. Document Damage',
          content: [
            'Take photos of all damage areas',
            'Upload photos directly in the claim detail page',
            'Add descriptions and notes to each photo',
            'Include weather data for storm verification',
            'Organize photos by damage type'
          ]
        },
        {
          title: '3. Generate Xactimate Export',
          content: [
            'Click "Export to Xactimate" button',
            'System generates CSV file with all claim data',
            'Import into Xactimate desktop software',
            'Includes measurements, line items, pricing',
            'Matches insurance industry standard formats'
          ]
        },
        {
          title: '4. Submission Document',
          content: [
            'Go to "Submission Document" tab in claim detail',
            'View professional formatted document',
            'Includes all measurements, photos, and calculations',
            'Download PDF for adjuster submission',
            'Send directly to insurance company'
          ]
        },
        {
          title: '5. Using FieldCam for Claims',
          content: [
            'Click "FieldCam" button from any claim',
            'Record on-site video and audio documentation',
            'AI generates professional damage reports',
            'All documentation automatically links to the claim',
            'Strengthens your case with comprehensive evidence'
          ]
        }
      ]
    },
    {
      id: 'reviews-referrals',
      title: 'Reviews & Referrals',
      icon: <Star className="w-6 h-6" />,
      duration: '15 min',
      lessons: [
        {
          title: '1. Configure Review Platform URLs',
          content: [
            'Go to Dashboard > Reviews & Referrals',
            'Click the "Settings" button in the top right corner',
            'Add your Google Business Review URL (get from Google Business Profile > "Ask for reviews")',
            'Add your Facebook Review URL (from your business page Reviews tab)',
            'Add your Yelp Business URL (from your Yelp business listing)',
            'Enable/disable automatic campaign trigger when projects complete'
          ]
        },
        {
          title: '2. Start a Review Campaign',
          content: [
            'Option A: From Estimate/Project Detail - Click "Request Review" button',
            'Option B: Campaigns start automatically when project status is "Completed"',
            'System sends automated email sequence: Day 1, 3, 7, and 14',
            'Emails stop automatically when customer leaves a review',
            'Monitor campaign status in Reviews & Referrals dashboard'
          ]
        },
        {
          title: '3. How Customers Leave Reviews',
          content: [
            'Customer receives friendly email with your branding',
            'Email contains buttons for Google, Facebook, and Yelp',
            'Customer clicks preferred platform and leaves review',
            'System tracks clicks and marks review as received',
            'You can also manually mark reviews as received'
          ]
        },
        {
          title: '4. Referral System',
          content: [
            'Referral campaigns start after review is received or 7 days after project completion',
            'Customer receives email asking for referrals',
            'Referral form captures: Name, email, phone, address of referred person',
            'Referrals automatically added to your Lead Pipeline',
            'Track referral status and conversions in the dashboard'
          ]
        },
        {
          title: '5. Dashboard & Statistics',
          content: [
            'View all active campaigns and their status',
            'Track reviews received count and conversion rate',
            'Monitor referrals received and new leads generated',
            'Pause or resume campaigns as needed',
            'Filter by status: Active, Completed, Paused'
          ]
        }
      ]
    },
    {
      id: 'team-management',
      title: 'Team Management',
      icon: <UserCog className="w-6 h-6" />,
      duration: '15 min',
      badge: 'NEW',
      lessons: [
        {
          title: '1. Adding Team Members',
          content: [
            'Navigate to Dashboard > Team Members',
            'Click "Invite Team Member" button',
            'Enter their email address and select role',
            'Choose permissions: Sales Rep, Estimator, or Admin',
            'Team member receives email invitation to join'
          ]
        },
        {
          title: '2. Managing Roles & Permissions',
          content: [
            'Sales Rep: Can view and manage their own leads and estimates',
            'Estimator: Can create and edit estimates, view all leads',
            'Admin: Full access to all features including team management',
            'Edit team member roles from their profile card',
            'Deactivate members who leave (preserves their data)'
          ]
        },
        {
          title: '3. Viewing Team Performance',
          content: [
            'Admins can view all team members\' pipelines',
            'Use the user filter dropdown in Lead Pipeline',
            'Select "All Team Members" to see combined view',
            'Generate reports showing individual and team metrics',
            'Track won deals, conversion rates, and pipeline value per rep'
          ]
        },
        {
          title: '4. Reassigning Leads',
          content: [
            'Open any lead from the Admin view',
            'Find the "Reassign Lead" section (purple box)',
            'Select a team member from the dropdown',
            'Lead is instantly transferred to their pipeline',
            'Great for balancing workloads or vacation coverage'
          ]
        }
      ]
    },
    {
      id: 'advanced',
      title: 'Advanced Features',
      icon: <BarChart className="w-6 h-6" />,
      duration: '15 min',
      lessons: [
        {
          title: '1. Custom Rates & Pricing',
          content: [
            'Go to Settings > My Rates',
            'Set default labor rates per square',
            'Configure material costs and markups',
            'Create pricing tiers for different projects',
            'Apply automatically to all new estimates'
          ]
        },
        {
          title: '2. Stripe Payment Integration',
          content: [
            'Navigate to Dashboard > Payment Setup',
            'Connect your Stripe account for online payments',
            'Enable customers to pay deposits or full invoices online',
            'Track payment status on each estimate',
            'Automatic receipts sent to customers'
          ]
        },
        {
          title: '3. Partner Program',
          content: [
            'Apply to become a HomeFixer360 partner',
            'Earn commissions on referrals',
            'Access exclusive partner pricing',
            'Get co-branded marketing materials',
            'Join partner community and training'
          ]
        },
        {
          title: '4. API Integration',
          content: [
            'Available on Enterprise plans',
            'Integrate measurements into your CRM',
            'Automate estimate generation workflow',
            'Access full REST API documentation',
            'Get developer support and webhooks'
          ]
        }
      ]
    }
  ];

  const activeTutorial = tutorials.find(t => t.id === activeSection);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-orange-500 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center mb-4">
            <Video className="w-12 h-12 mr-4" />
            <h1 className="text-4xl font-bold">Training & Tutorials</h1>
          </div>
          <p className="text-center text-blue-100 text-lg">
            Learn how to use HomeFixer360 to grow your home service business
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Modules</h2>
              <nav className="space-y-2">
                {tutorials.map((tutorial) => (
                  <button
                    key={tutorial.id}
                    onClick={() => setActiveSection(tutorial.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                      activeSection === tutorial.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center">
                      {tutorial.icon}
                      <span className="ml-3 font-medium">{tutorial.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {tutorial.badge && (
                        <span className="bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded-full">
                          {tutorial.badge}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </button>
                ))}
              </nav>

              {/* Quick Links */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">Quick Links</h3>
                <div className="space-y-2 text-sm">
                  <Link to="/dashboard" className="block text-blue-600 hover:underline">
                    Go to Dashboard
                  </Link>
                  <Link to="/dashboard/reviews" className="block text-blue-600 hover:underline">
                    Reviews & Referrals
                  </Link>
                  <Link to="/contact" className="block text-blue-600 hover:underline">
                    Contact Support
                  </Link>
                  <Link to="/" className="block text-blue-600 hover:underline">
                    Back to Home
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTutorial && (
              <div className="bg-white rounded-lg shadow-lg p-8">
                {/* Tutorial Header */}
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      {activeTutorial.title}
                    </h2>
                    <p className="text-gray-600 flex items-center">
                      <Play className="w-4 h-4 mr-2" />
                      {activeTutorial.duration} • {activeTutorial.lessons.length} lessons
                    </p>
                  </div>
                </div>

                {/* Lessons */}
                <div className="space-y-8">
                  {activeTutorial.lessons.map((lesson, index) => (
                    <div key={index} className="border-l-4 border-blue-600 pl-6 py-2">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">
                        {lesson.title}
                      </h3>
                      <ul className="space-y-3">
                        {lesson.content.map((item, i) => (
                          <li key={i} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* Tips Box */}
                <div className="mt-12 bg-blue-50 border-l-4 border-blue-600 rounded-lg p-6">
                  <h3 className="font-bold text-blue-900 mb-2">💡 Pro Tips</h3>
                  <ul className="space-y-2 text-blue-800">
                    <li>• Use keyboard shortcuts to navigate faster</li>
                    <li>• Save frequently used addresses as favorites</li>
                    <li>• Set up email templates for faster customer communication</li>
                    <li>• Enable notifications to never miss a lead</li>
                  </ul>
                </div>

                {/* Next Steps */}
                <div className="mt-8 flex gap-4">
                  <button className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                    Mark as Complete
                  </button>
                  <button className="px-6 py-3 bg-white text-blue-600 border-2 border-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                    Next Module
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Video Tutorials Coming Soon */}
        <div className="mt-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg text-white p-8 text-center">
          <Video className="w-16 h-16 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Video Tutorials Coming Soon!</h2>
          <p className="text-orange-100 mb-4">
            We're creating step-by-step video guides to help you master HomeFixer360
          </p>
          <button className="px-6 py-3 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-50 transition-colors">
            Notify Me When Available
          </button>
        </div>
      </div>
    </div>
  );
};

export default Tutorial;